import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Modal,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
  Grid,
  TextField,
  Skeleton,
  Chip,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Tooltip,
  Checkbox,
  ListItemText
} from '@mui/material';
import { localeTextSpanish } from '../locales/localeTextSpanish';
import {
  Add as AddIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Error as ErrorIcon,
  DateRange as DateRangeIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import EditModal from './EditModal';

const CfdiTable = forwardRef(({ onAddClick }, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const initialPage = parseInt(sessionStorage.getItem('cfdiPage') || '0', 10);
  const initialPageSize = parseInt(sessionStorage.getItem('cfdiPageSize') || '100', 10);
  const initialSelected = JSON.parse(sessionStorage.getItem('cfdiSelectedRows') || '[]');

  // Estados principales
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState(initialSelected);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [uuidFilter, setUuidFilter] = useState('');
  const [rowSelectionModel, setRowSelectionModel] = useState(initialSelected);
  const [filters, setFilters] = useState({
    emisor: [],
    fechaDesde: '',
    fechaHasta: ''
  });
  const [presetRange, setPresetRange] = useState('');
  const [tipoDocumentoFilter, setTipoDocumentoFilter] = useState([]);
  const [emisorSearch, setEmisorSearch] = useState('');
  const [tipoDocumentoSearch, setTipoDocumentoSearch] = useState('');
  const [editingCfdiId, setEditingCfdiId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [availableYears, setAvailableYears] = useState([]);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [rowCount, setRowCount] = useState(0);
  const [uniqueEmisores, setUniqueEmisores] = useState([]);
  const [uniqueTiposDocumento, setUniqueTiposDocumento] = useState([]);

  // Roles del usuario
  const isAdmin = currentUser?.roles?.includes('Administrador');
  const isAnalistaProveedores = currentUser?.roles?.includes('AnalistaProveedores');

  // Función para obtener el usuario actual
  const fetchCurrentUser = async () => {
    try {
      setUserLoading(true);
      const res = await axios.get('https://facturas.verdevalle.com.mx/api/validate-session', { 
        withCredentials: true 
      });
      setCurrentUser(res.data.user);
    } catch (err) {
      console.error('Error al obtener usuario actual:', err);
      navigate('/'); // Redirigir a login si no hay sesión válida
    } finally {
      setUserLoading(false);
    }
  };

  // Función para obtener los CFDI con filtros del servidor
  const fetchData = async () => {
    try {
      setLoading(true);

      // Obtener años disponibles si no los tenemos
      if (availableYears.length === 0) {
        const yearsResponse = await axios.get('https://facturas.verdevalle.com.mx/api/cfdis/years', { 
          withCredentials: true 
        });
        setAvailableYears(yearsResponse.data);
      }

      // Parámetros para paginación y filtros
      const params = {
        year: yearFilter === 'all' ? null : yearFilter,
        page: page + 1, // backend inicia en 1
        pageSize,
        uuid: uuidFilter.trim() || undefined,
        emisor: filters.emisor.length ? filters.emisor : undefined,
        tipoComprobante: tipoDocumentoFilter.length ? tipoDocumentoFilter : undefined,
        fechaDesde: filters.fechaDesde || undefined,
        fechaHasta: filters.fechaHasta || undefined,
      };

      // Llamada al backend paginado con filtros
      const response = await axios.get('https://facturas.verdevalle.com.mx/api/cfdis', { 
        withCredentials: true,
        params,
      });

      const { total, rows: fetchedRows, uniqueEmisores: serverEmisores = [], uniqueTipos: serverTipos = [] } = response.data;

      // Actualizar listas únicas desde el servidor
      setUniqueEmisores(serverEmisores.filter(e => e)); // Filtrar valores nulos
      setUniqueTiposDocumento(serverTipos.filter(t => t)); // Filtrar valores nulos

      // Normalización de datos
      const normalizedData = fetchedRows.map(item => ({
        ...item,
        id: item.id || item.uuid || Math.random().toString(36).substr(2, 9), // Asegurar que siempre haya un id
        fecha: item.Fecha || item.fecha || '',
        fecha_registro: item.fecha_registro || item.Fecha || '',
        nombreEmisor: item.NombredeEmisor || item.nombreEmisor || '',
        tipoComprobante: item.TipoDeComprobante || item.tipoComprobante || '',
        serie: item.Serie || item.serie || '',
        folio: item.Folio || item.folio || '',
        ordenCompra: item.ordendecompra || item.ordenCompra || '',
        entradaMercancia: item.entradademercancia || item.entradaMercancia || '',
        total: item.Total || item.total || 0,
        pdfPath: item.rutadealmacenamientopdf || item.pdfPath || '',
        xmlPath: item.rutadealmacenamientoxml || item.xmlPath || '',
        year: item.year || (item.Fecha ? new Date(item.Fecha).getFullYear() : new Date().getFullYear())
      }));

      setRows(normalizedData);
      setRowCount(total);
    } catch (error) {
      console.error('Error al obtener CFDIs:', error);
      setError(error.response?.data?.message || 'Error al cargar facturas. Intente nuevamente.');
      setSnackbarOpen(true);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Efecto inicial para cargar usuario y datos
  useEffect(() => {
    const initializeData = async () => {
      await fetchCurrentUser();
      await fetchData();
    };
    initializeData();
  }, [navigate]);
  
  // Efecto para recargar datos cuando cambian los filtros o paginación
  useEffect(() => {
    if (currentUser) {
      const timer = setTimeout(() => {
        fetchData();
      }, 300); // Debounce para evitar múltiples llamadas rápidas

      return () => clearTimeout(timer);
    }
  }, [yearFilter, page, pageSize, uuidFilter, filters, tipoDocumentoFilter]);

  // Funciones de UI
  const handleOpenPdfModal = (url) => {
    const correctedUrl = url.includes('https://facturas.verdevalle.com.mx/') 
      ? url 
      : `https://facturas.verdevalle.com.mx/${url.replace(/^\/+/g, '')}`;
    setPdfUrl(correctedUrl);
    setModalOpen(true);
  };

  const handleClosePdfModal = () => {
    setPdfUrl(null);
    setModalOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Funciones de acciones
  const handleBulkDownload = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        'https://facturas.verdevalle.com.mx/api/download-zip',
        { ids: selectedRows },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'cfdis.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error al descargar archivos:', error);
      setError('Error al generar archivo ZIP. Por favor intente nuevamente.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendToSap = async () => {
    try {
      if (selectedRows.length === 0) {
        setError('Selecciona al menos una factura para enviar a SAP');
        setSnackbarOpen(true);
        return;
      }

      setLoading(true);
      
      const selectedCfdis = rows.filter(row => selectedRows.includes(row.id));
      const hasPagos = selectedCfdis.some(cfdi => cfdi.tipoComprobante === 'P');
      
      let response;
      
      if (hasPagos) {
        response = await axios.post(
          'https://facturas.verdevalle.com.mx/api/process-payments',
          { ids: selectedRows }
        );
      } else {
        response = await axios.post(
          'https://facturas.verdevalle.com.mx/api/generate-sap-files',
          { ids: selectedRows }
        );
      }

      setError(`Operación completada: ${response.data.message}`);
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error('Error al enviar a SAP:', error);
      setError(error.response?.data?.message || 'Error al procesar documentos en SAP');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSelection = () => {
    sessionStorage.removeItem('cfdiSelectedRows');
    sessionStorage.removeItem('cfdiPage');
    sessionStorage.removeItem('cfdiPageSize');
    setSelectedRows([]);
    setRowSelectionModel([]);
    setPage(0);
    setPageSize(100);
    setUuidFilter('');
    setFilters({
      emisor: [],
      fechaDesde: '',
      fechaHasta: ''
    });
    setPresetRange('');
    setTipoDocumentoFilter([]);
    setEmisorSearch('');
    setTipoDocumentoSearch('');
  };

  useImperativeHandle(ref, () => ({
    refreshData: fetchData,
    clearSelection: handleClearSelection
  }));

  const applyPresetRange = (range) => {
    const today = new Date();
    const newFilters = { ...filters };
    
    switch (range) {
      case 'hoy':
        newFilters.fechaDesde = today.toISOString().split('T')[0];
        newFilters.fechaHasta = today.toISOString().split('T')[0];
        break;
      case 'ayer':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        newFilters.fechaDesde = yesterday.toISOString().split('T')[0];
        newFilters.fechaHasta = yesterday.toISOString().split('T')[0];
        break;
      case 'semana':
        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 7);
        newFilters.fechaDesde = lastWeek.toISOString().split('T')[0];
        newFilters.fechaHasta = today.toISOString().split('T')[0];
        break;
      case 'mes':
        const lastMonth = new Date(today);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        newFilters.fechaDesde = lastMonth.toISOString().split('T')[0];
        newFilters.fechaHasta = today.toISOString().split('T')[0];
        break;
      case 'trimestre':
        const lastQuarter = new Date(today);
        lastQuarter.setMonth(lastQuarter.getMonth() - 3);
        newFilters.fechaDesde = lastQuarter.toISOString().split('T')[0];
        newFilters.fechaHasta = today.toISOString().split('T')[0];
        break;
      default:
        newFilters.fechaDesde = '';
        newFilters.fechaHasta = '';
    }
    
    setFilters(newFilters);
    setPresetRange(range);
  };

  // Componentes de renderizado
  const renderLoadingSkeleton = () => (
    <Grid container spacing={1}>
      {[...Array(3)].map((_, index) => (
        <Grid item xs={12} key={index}>
          <Card variant="outlined" sx={{ borderRadius: 1 }}>
            <CardContent>
              <Skeleton variant="text" width="80%" height={20} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                <Skeleton variant="text" width="40%" height={16} />
                <Skeleton variant="text" width="40%" height={16} />
              </Box>
              <Skeleton variant="text" width="60%" height={16} sx={{ mt: 0.5 }} />
              <Skeleton variant="text" width="60%" height={16} />
            </CardContent>
            <CardActions sx={{ py: 0 }}>
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="circular" width={20} height={20} />
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderMobileCards = () => (
    <Grid container spacing={1}>
      {rows.length === 0 ? (
        <Grid item xs={12}>
          <Typography variant="body2" align="center" sx={{ py: 2 }}>
            {loading ? 'Cargando...' : 'Sin resultados'}
          </Typography>
        </Grid>
      ) : (
        rows.map((row) => (
          row && (
            <Grid item xs={12} key={row.id}>
              <Card variant="outlined" sx={{ borderRadius: 1 }}>
                <CardContent sx={{ pb: 1 }}>
                 <Typography variant="body2" gutterBottom noWrap sx={{ fontWeight: 700 }}>
                
                    {row.uuid || '-'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      <strong>Fecha:</strong> {row.fecha ? new Date(row.fecha).toLocaleDateString('es-MX') : '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      <strong>Registro:</strong> {row.fecha_registro ? new Date(row.fecha_registro).toLocaleDateString('es-MX') : '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      <strong>Total:</strong> ${row.total ? parseFloat(row.total).toLocaleString('es-MX', { minimumFractionDigits: 2 }) : '0.00'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                    <strong>Emisor:</strong> {row.nombreEmisor || '-'}
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                    <strong>Tipo:</strong> {row.tipoComprobante === 'I' ? 'Ingreso' : row.tipoComprobante === 'E' ? 'Egreso' : row.tipoComprobante === 'P' ? 'Pago' : row.tipoComprobante || '-'}
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                    <strong>Serie/Folio:</strong> {row.serie || ''}{row.serie && row.folio ? '/' : ''}{row.folio || ''}
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                    <strong>OC/Entrada:</strong> {row.ordenCompra || ''}{row.ordenCompra && row.entradaMercancia ? '|' : ''}{row.entradaMercancia || ''}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', pt: 0 }}>
                  <Box>
                    {row.pdfPath ? (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenPdfModal(row.pdfPath)}
                        aria-label="Ver PDF"
                        sx={{ p: 0.5 }}
                      >
                        <PictureAsPdfIcon fontSize="small" color="error" />
                      </IconButton>
                    ) : (
                      <Typography color="textSecondary" variant="body2" sx={{ fontSize: '0.75rem' }}>PDF: -</Typography>
                    )}
                  </Box>
                  <Box>
                    {row.xmlPath ? (
                      <IconButton
                        size="small"
                        component="a"
                        href={`https://facturas.verdevalle.com.mx/${row.xmlPath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Descargar XML"
                        sx={{ p: 0.5 }}
                      >
                        <DescriptionIcon fontSize="small" color="action" />
                      </IconButton>
                    ) : (
                      <Typography color="textSecondary" variant="body2" sx={{ fontSize: '0.75rem' }}>XML: -</Typography>
                    )}
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => setEditingCfdiId(row.id)}
                      aria-label="Editar factura"
                      sx={{ p: 0.5 }}
                    >
                      <EditIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          )
        ))
      )}
    </Grid>
  );

  // Columnas para DataGrid
  const columns = [
    {
      field: 'uuid',
      headerName: 'UUID',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        
          <Typography noWrap sx={{ width: 'calc(100% - 28px)', fontSize: '0.75rem', fontWeight: 700 }}>
            {params.row.uuid || '-'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'fecha',
      headerName: 'Fecha CFDI',
      width: 90,
      renderCell: (params) => {
        const val = params.value;
        if (!val) return '-';
        const d = new Date(val);
        return (
          <Typography sx={{ fontSize: '0.75rem' }}>
            {isNaN(d.getTime()) ? '-' : d.toLocaleDateString('es-MX')}
          </Typography>
        );
      }
    },
    {
      field: 'fecha_registro',
      headerName: 'Fecha Registro',
      width: 100,
      renderCell: (params) => {
        const val = params.value;
        if (!val) return '-';
        const d = new Date(val);
        return (
          <Typography sx={{ fontSize: '0.75rem' }}>
            {isNaN(d.getTime()) ? '-' : d.toLocaleDateString('es-MX')}
          </Typography>
        );
      }
    },
    {
      field: 'tipoComprobante',
      headerName: 'Tipo',
      width: 80,
      renderCell: (params) => (
        <Chip 
          label={
            params.value === 'I' ? 'Ingreso' : 
            params.value === 'E' ? 'Egreso' : 
            params.value === 'P' ? 'Pago' : params.value || '-'
          } 
          size="small" 
          color={
            params.value === 'I' ? 'primary' : 
            params.value === 'E' ? 'secondary' : 
            params.value === 'P' ? 'success' : 'default'
          }
          sx={{ fontSize: '0.7rem' }}
        />
      )
    },
  {
      field: 'serie_folio',
      headerName: 'Serie/Folio',
      width: 100,
      renderCell: (params) => (
        <Typography noWrap sx={{ fontSize: '0.75rem' }}>{params.value || '-'}</Typography>
      )
    },
    { 
      field: 'nombreEmisor', 
      headerName: 'Emisor', 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography noWrap sx={{ fontSize: '0.75rem' }}>{params.value || '-'}</Typography>
      )
    },
   {
      field: 'oc_entrada',
      headerName: 'OC/Entrada',
      width: 100,
      renderCell: (params) => (
        <Typography noWrap sx={{ fontSize: '0.75rem' }}>{params.value || '-'}</Typography>
      )
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 90,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.75rem' }}>
          {`$${parseFloat(params.value || 0).toLocaleString('es-MX', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`}
        </Typography>
      )
    },
    {
      field: 'pdfPath',
      headerName: 'PDF',
      width: 60,
      renderCell: (params) =>
        params.value ? (
          <PictureAsPdfIcon
            color="error"
            sx={{ cursor: 'pointer', fontSize: '1rem' }}
            onClick={() => handleOpenPdfModal(params.value)}
            aria-label="Ver PDF"
          />
        ) : (
          <Typography color="textSecondary" sx={{ fontSize: '0.75rem' }}>-</Typography>
        )
    },
    {
      field: 'xmlPath',
      headerName: 'XML',
      width: 60,
      renderCell: (params) =>
        params.value ? (
          <a
            href={`https://facturas.verdevalle.com.mx/${params.value}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Descargar XML"
          >
            <DescriptionIcon color="action" sx={{ cursor: 'pointer', fontSize: '1rem' }} />
          </a>
        ) : (
          <Typography color="textSecondary" sx={{ fontSize: '0.75rem' }}>-</Typography>
        )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => setEditingCfdiId(params.row.id)}
          aria-label="Editar factura"
        >
          <EditIcon fontSize="small" color="primary" />
        </IconButton>
      )
    }
  ];

  // Filtrar emisores según búsqueda
  const filteredEmisores = uniqueEmisores.filter(emisor => 
    emisor && emisor.toLowerCase().includes(emisorSearch.toLowerCase())
  );

  // Filtrar tipos de documento según búsqueda
  const filteredTiposDocumento = uniqueTiposDocumento.filter(tipo => 
    tipo && tipo.toLowerCase().includes(tipoDocumentoSearch.toLowerCase())
  );

  // Validar rango de fechas
  const isDateRangeInvalid = filters.fechaDesde && filters.fechaHasta && 
    new Date(filters.fechaHasta) < new Date(filters.fechaDesde);

  // Renderizado condicional mientras se carga el usuario
  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      backgroundColor: 'background.paper', 
      borderRadius: 1, 
      boxShadow: 1, 
      p: 1,
      overflow: 'hidden'
    }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: 1,
          mb: 1
        }}
      >
        <Typography variant="h6" component="h2" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
          Facturas Registradas
        </Typography>
        <Button 
          variant="contained" 
          size="small"
          startIcon={<AddIcon fontSize="small" />} 
          onClick={onAddClick}
          fullWidth={isMobile}
          aria-label="Agregar factura"
          sx={{ fontSize: '0.75rem' }}
        >
          Agregar Factura
        </Button>
     </Box>

      <Paper elevation={0} sx={{ 
 p: 2,
    mb: 1.5,
    borderRadius: 3,
    backgroundColor: '#fff',
    boxShadow: '0 10px 24px rgba(2, 6, 23, 0.06)',
    border: '1px solid #eef2f7'
      }}>
        <Grid container spacing={1} alignItems="center">
          {/* Filtro por año */}
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.75rem' }}>Año</InputLabel>
              <Select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                label="Año"
                sx={{ fontSize: '0.75rem' }}
              >
                <MenuItem value={new Date().getFullYear().toString()} sx={{ fontSize: '0.75rem' }}>
                  Actual ({new Date().getFullYear()})
                </MenuItem>
                <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>
                  Todos los años
                </MenuItem>
                {availableYears.map(year => (
                  <MenuItem key={year} value={year.toString()} sx={{ fontSize: '0.75rem' }}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>

<TextField
  fullWidth
  size="small"
  label="Buscar UUIDs/Folios"
  variant="outlined"
  value={uuidFilter}
  onChange={(e) => setUuidFilter(e.target.value)}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon fontSize="small" />
      </InputAdornment>
    ),
    endAdornment: uuidFilter && (
      <IconButton 
        size="small"
        onClick={() => setUuidFilter('')}
      >
        <ClearIcon fontSize="small" />
      </IconButton>
    )
  }}
  sx={{
    '& .MuiInputBase-input': {
      fontSize: '0.75rem'
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.65rem',
      lineHeight: 1.2,
      mt: 0.5
    }
  }}

/>
          </Grid>

         {/* Dropdown de Emisor */}
<Grid item xs={6} sm={3} md={2}>
  <FormControl fullWidth size="small">
    <InputLabel sx={{ fontSize: '0.75rem' }}>Emisor</InputLabel>
    <Select
      multiple
      value={filters.emisor}
      onChange={(e) => {
        setFilters({...filters, emisor: e.target.value});
        // Solo actualizamos si realmente hay un cambio en la selección
        if (e.target.value.length !== filters.emisor.length) {
          setPage(0); // Resetear a primera página al cambiar filtros
        }
      }}
      label="Emisor"
      renderValue={(selected) => selected.length > 0 ? selected.join(', ') : 'Todos'}
      sx={{ fontSize: '0.75rem' }}
      MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: 300
          }
        },
        // Evitar que el menú se cierre al hacer clic en el input
        onClick: (e) => e.stopPropagation()
      }}
    >
      <Box sx={{ p: 1 }} onClick={(e) => e.stopPropagation()}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar emisor..."
          value={emisorSearch}
          onChange={(e) => {
            setEmisorSearch(e.target.value);
            // No activamos fetchData aquí, solo filtramos la lista local
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{
            mb: 1,
            '& .MuiInputBase-input': {
              fontSize: '0.75rem'
            }
          }}
          // Mantener el focus mientras se escribe
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      </Box>
	                  <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>
                  <em>Seleccione emisores</em>
                </MenuItem>
      {filteredEmisores.length > 0 ? (
        filteredEmisores.map(emisor => (
          <MenuItem key={emisor} value={emisor} sx={{ fontSize: '0.75rem' }}>
            <Checkbox checked={filters.emisor.includes(emisor)} />
            <ListItemText primary={emisor} />
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled sx={{ fontSize: '0.75rem' }}>
          No hay emisores disponibles
        </MenuItem>
      )}
    </Select>
  </FormControl>
</Grid>

        {/* Dropdown de Tipo Documento */}
<Grid item xs={6} sm={3} md={1}>
  <FormControl fullWidth size="small">
    <InputLabel sx={{ fontSize: '0.75rem' }}>Tipo Doc.</InputLabel>
    <Select
      multiple
      value={tipoDocumentoFilter}
      onChange={(e) => {
        setTipoDocumentoFilter(e.target.value);
        // Solo actualizamos si realmente hay un cambio en la selección
        if (e.target.value.length !== tipoDocumentoFilter.length) {
          setPage(0); // Resetear a primera página al cambiar filtros
        }
      }}
      label="Tipo Doc."
      renderValue={(selected) => 
        selected.length > 0 
          ? selected.map(t => 
              t === 'I' ? 'Ingreso' : 
              t === 'E' ? 'Egreso' : 
              t === 'P' ? 'Pago' : t
            ).join(', ') 
          : 'Todos'
      }
      sx={{ fontSize: '0.75rem' }}
      MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: 300
          }
        },
        // Evitar que el menú se cierre al hacer clic en el input
        onClick: (e) => e.stopPropagation()
      }}
    >
      <Box sx={{ p: 1 }} onClick={(e) => e.stopPropagation()}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar tipo..."
          value={tipoDocumentoSearch}
          onChange={(e) => {
            setTipoDocumentoSearch(e.target.value);
            // No activamos fetchData aquí, solo filtramos la lista local
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{
            mb: 1,
            '& .MuiInputBase-input': {
              fontSize: '0.75rem'
            }
          }}
          // Mantener el focus mientras se escribe
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      </Box>
	    <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>
                  <em>Seleccione tipos</em>
				  </MenuItem>
      {filteredTiposDocumento.length > 0 ? (
        filteredTiposDocumento.map(tipo => (
          <MenuItem key={tipo} value={tipo} sx={{ fontSize: '0.75rem' }}>
            <Checkbox checked={tipoDocumentoFilter.includes(tipo)} />
            <ListItemText primary={
              tipo === 'I' ? 'Ingreso' : 
              tipo === 'E' ? 'Egreso' : 
              tipo === 'P' ? 'Pago' : tipo
            } />
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled sx={{ fontSize: '0.75rem' }}>
          No hay tipos disponibles
        </MenuItem>
      )}
    </Select>
  </FormControl>
</Grid>

          <Grid item xs={6} sm={4} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.75rem' }}>Rango</InputLabel>
              <Select
                value={presetRange}
                onChange={(e) => applyPresetRange(e.target.value)}
                label="Rango"
                startAdornment={
                  <InputAdornment position="start">
                    <DateRangeIcon fontSize="small" />
                  </InputAdornment>
                }
                sx={{ fontSize: '0.75rem' }}
              >
                <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Personalizado</MenuItem>
                <MenuItem value="hoy" sx={{ fontSize: '0.75rem' }}>Hoy</MenuItem>
                <MenuItem value="ayer" sx={{ fontSize: '0.75rem' }}>Ayer</MenuItem>
                <MenuItem value="semana" sx={{ fontSize: '0.75rem' }}>Últimos 7 días</MenuItem>
                <MenuItem value="mes" sx={{ fontSize: '0.75rem' }}>Últimos 30 días</MenuItem>
                <MenuItem value="trimestre" sx={{ fontSize: '0.75rem' }}>Últimos 90 días</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={1}>
            <Tooltip 
              title={isDateRangeInvalid ? "La fecha 'Hasta' no puede ser menor que la fecha 'Desde'" : ""}
              placement="top"
              arrow
            >
              <TextField
                fullWidth
                size="small"
                label="Desde"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.fechaDesde}
                onChange={(e) => {
                  setFilters({...filters, fechaDesde: e.target.value});
                  setPresetRange('');
                }}
                error={isDateRangeInvalid}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={6} sm={4} md={1}>
            <Tooltip 
              title={isDateRangeInvalid ? "La fecha 'Hasta' no puede ser menor que la fecha 'Desde'" : ""}
              placement="top"
              arrow
            >
              <TextField
                fullWidth
                size="small"
                label="Hasta"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.fechaHasta}
                onChange={(e) => {
                  setFilters({...filters, fechaHasta: e.target.value});
                  setPresetRange('');
                }}
                error={isDateRangeInvalid}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={6} sm={4} md={1}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="error"
              onClick={() => {
                handleClearSelection();
                setTipoDocumentoFilter([]);
              }}
              startIcon={<FilterListIcon fontSize="small" />}
              sx={{ 
                height: '40px',
                fontSize: '0.7rem'
              }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>

        {isDateRangeInvalid && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 1,
            color: 'error.main',
            fontSize: '0.75rem'
          }}>
            <ErrorIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2">
              La fecha 'Hasta' no puede ser menor que la fecha 'Desde'
            </Typography>
          </Box>
        )}
      </Paper>

      {selectedRows.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          mb: 1, 
          gap: 1 
        }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            flexDirection: isMobile ? 'column' : 'row',
            width: isMobile ? '100%' : 'auto'
          }}>
            <Button 
              variant="outlined" 
              size="small"
              color="primary" 
              onClick={handleBulkDownload}
              fullWidth={isMobile}
              disabled={loading}
              aria-label="Descargar facturas seleccionadas"
              sx={{ fontSize: '0.75rem' }}
            >
              Descargar ({selectedRows.length})
            </Button>
            
            {(isAdmin || isAnalistaProveedores) && (
              <Button 
                variant="contained" 
                size="small"
                color="primary"
                onClick={handleSendToSap}
                startIcon={<DescriptionIcon fontSize="small" />}
                fullWidth={isMobile}
                disabled={loading}
                aria-label="Enviar facturas a SAP"
                sx={{ fontSize: '0.75rem' }}
              >
                {selectedRows.some(id => {
                  const cfdi = rows.find(r => r.id === id);
                  return cfdi?.tipoComprobante === 'P';
                }) ? 'Enviar Pagos a SAP' : 'Enviar Facturas a SAP'}
              </Button>
            )}
          </Box>
        </Box>
      )}

      {loading && !isMobile ? (
        <Box sx={{ height: 300, width: '100%' }}>
          <DataGrid
            rows={[]}
            columns={columns}
            loading={true}
            localeText={localeTextSpanish}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#e0e0e0',
                color: '#333',
                fontWeight: 'bold',
                fontSize: '0.75rem'
              }
            }}
          />
        </Box>
      ) : loading ? (
        renderLoadingSkeleton()
      ) : isMobile ? (
        renderMobileCards()
      ) : (
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          checkboxSelection
          localeText={localeTextSpanish}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
            sessionStorage.setItem('cfdiPage', model.page);
            sessionStorage.setItem('cfdiPageSize', model.pageSize);
          }}
          pageSizeOptions={[25, 50, 100]}
          loading={loading}
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => {
            setRowSelectionModel(newSelection);
            setSelectedRows(newSelection);
            sessionStorage.setItem('cfdiSelectedRows', JSON.stringify(newSelection));
          }}
          rowSelectionModel={rowSelectionModel}
          sx={{
           // card redondeado
    border: '1px solid #eef2f7',
    borderRadius: 3,
    overflow: 'hidden',
    backgroundColor: '#fff',

    // header
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#f3f4f6',
      color: '#111827',
      fontWeight: 700,
      fontSize: '0.75rem',
      letterSpacing: 0.2,
      borderBottom: '1px solid #e5e7eb',
      minHeight: 44,
      '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 700,
        textTransform: 'uppercase'
      }
    },
            '& .MuiDataGrid-cell': {
              py: 0.5,
              fontSize: '0.75rem'
            },
            '& .MuiDataGrid-footerContainer': {
              minHeight: 'auto'
            }
          }}
          aria-label="Tabla de facturas"
        />
      )}

      <Modal 
        open={modalOpen} 
        onClose={handleClosePdfModal}
        aria-labelledby="pdf-modal-title"
        aria-describedby="pdf-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '95%' : '90%',
            height: isMobile ? '90%' : '85%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ 
            p: 1, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            borderBottom: '1px solid #ccc',
            backgroundColor: '#e0e0e0'
          }}>
            <Typography id="pdf-modal-title" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Visualizador de PDF
            </Typography>
            <Button 
              variant="contained" 
              size="small"
              color="primary" 
              onClick={() => window.open(pdfUrl, '_blank')}
              aria-label="Descargar PDF"
              sx={{ fontSize: '0.75rem' }}
            >
              Descargar
            </Button>
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            {pdfUrl ? (
              <iframe 
                src={pdfUrl} 
                title="Factura PDF" 
                width="100%" 
                height="100%" 
                style={{ border: 'none' }}
                aria-describedby="pdf-modal-description"
              />
            ) : (
              <Typography sx={{ p: 2 }}>No se pudo cargar el PDF.</Typography>
            )}
          </Box>
        </Box>
      </Modal>

      <EditModal
        open={!!editingCfdiId}
        onClose={() => {
          setEditingCfdiId(null);
          fetchData();
        }}
        cfdiId={editingCfdiId}
        refreshTable={fetchData}
      />

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar} 
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error?.includes('correctamente') ? 'success' : 'error'} 
          sx={{ 
            width: '100%',
            fontSize: '0.8rem'
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default CfdiTable;